import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePosting, useTitle } from '../../Hooks';
import { Card, Modal } from '../../components';
import { Header, Stat, Message, Table, Button, Input, Toast } from 'react-frontier';
import { addCommas, bindClick, bindFormChange } from '@tempu/components/Util';
import { Payout, SetLoading } from '../../Classes';
import BusinessAccess from '../../BusinessAccess';
import moment from 'moment';
import classNames from 'classnames';
import API from '../../API';
import Validator from '@tempu/components/Validator';

interface Worker{
	payment_id: number,
	user_id: number,
	amount: number,
	paid: boolean,
	cancelled: boolean,
	message: string,
	user_name: string,
	date_paid?: number,
	date_created: number,
	job_name?: string,
}

interface PostingPayoutInfoProps{
	
}

var PostingPayoutInfo = (props: PostingPayoutInfoProps)=>{
   var params = useParams<{ bid: string, pid: string, pyid: string }>();
   var posting_id = parseInt(params?.pid);
   var payout_id = parseInt(params?.pyid);
   var { hasAccess } = usePosting(posting_id);
   var { setTitle } = useTitle();
	var [loadError, setLoadError] = useState<string>(null);
   var [payout, setPayout] = useState<Payout>(null);
	var [workerModal, setWorkerModal] = useState<boolean>(false);
	var [selectedWorker, setSelectedWorker] = useState<Worker>(null);
	var [editWorker, setEditWorker] = useState<boolean>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [paidWorkerModal, setPaidWorkerModal] = useState<boolean>(null);
	var [cancelWorkerModal, setCancelWorkerModal] = useState<boolean>(null);

   useEffect(()=>{
		if(Number.isNaN(payout_id) || !payout_id) return;
		if(!hasAccess(BusinessAccess.ACCOUNTING.VIEW_PAYOUTS)) return;

		API.getPostingPayout(payout_id, posting_id).then(res=>{
			if(res.error) return setLoadError(res.message);
         setTitle('Liquidación #' + res.data.payout_id);
			setPayout(res.data);
		}).catch(err=>{
			return setLoadError('Hubo un error inesperado cargando las liquidaciones del empleo. (LCL-1)');
		})
	}, []);

	var showWorkerModal = (worker: Worker) =>{
		setEditWorker(false);
		setSelectedWorker(worker);
		setWorkerModal(true);
	}

	var edit = async(setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(selectedWorker, {
			amount: [{
				rule: 'number', label: 'Monto'
			}],
		});
		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.editPayoutWorker(selectedWorker).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Empleado actualizado!');
			var p = {...payout};
			var i = payout.users.findIndex(u => u.payment_id === selectedWorker.payment_id);
			p.users[i].amount = selectedWorker.amount;
			setPayout(p);
			setWorkerModal(false);
		}).catch(err=>{
			setModalPrompts(['Hubo un error al actualizar el empleado. (PCE-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var paidWorker = async(setLoading: SetLoading)=>{
		setLoading(true);
		API.paidPayoutWorker(selectedWorker.payment_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Pago realizado!');
			var p = {...payout};
			var i = payout.users.findIndex(u => u.payment_id === selectedWorker.payment_id);
			p.users[i].paid = true;
			setPayout(p);
			setWorkerModal(false);
			setPaidWorkerModal(false);
		}).catch(err=>{
			setModalPrompts(['Hubo un error al pagar a el empleado. (PCE-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var cancelWorker = async(setLoading: SetLoading)=>{
		setLoading(true);
		API.cancelWorkerPayout(selectedWorker.payment_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Pago cancelado!');
			var p = {...payout};
			var i = payout.users.findIndex(u => u.payment_id === selectedWorker.payment_id);
			p.users[i].cancelled = true;
			setPayout(p);
			setWorkerModal(false);
			setCancelWorkerModal(false);
		}).catch(err=>{
			setModalPrompts(['Hubo un error al cancelar a el empleado. (PCE-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onWorkerEdit = bindFormChange(selectedWorker, setSelectedWorker);

   if(!hasAccess(BusinessAccess.ACCOUNTING.VIEW_PAYOUTS)){
		return <Header text='Sin acceso' subtext='No tienes acceso a esta página' />
	}

	if(loadError){
		return <Header text='Error' subtext={loadError} />
	}

	if(!payout){
		return <Header loading text='Cargando datos' />;
	}

	var currentDebt = payout.users?.reduce((s, p) => { return !p.paid ? s + parseFloat(p.amount.toString()) : s; }, 0);
	var totalDebt = payout.users?.reduce((s, p) => { return s + parseFloat(p.amount.toString()); }, 0);
	var totalPaid = payout.users?.reduce((s, p) => { return p.paid ? s + parseFloat(p.amount.toString()) : s; }, 0);
	var paid = payout.users?.reduce((a, u) => a && u.paid, true);

   return <div className="tp payout-info-screen">
      <Stat.Group>
			<Stat value={'$'+addCommas(currentDebt)} label={'Pendiente de pago'} />
			<Stat value={'$'+addCommas(totalPaid)} label={'Pagado'} />
			<Stat value={payout.users.length} label={'Empleados'} />
		</Stat.Group>
		{new Set(payout.users?.map(u => u.user_id)).size !== payout.users?.length && <Message type='warning' header='Empleado repetido' centered style={{ maxWidth: 500, margin: 'auto', marginTop: 15 }}>
			En esta liquidación se ha identificado que uno o más empleados están duplicados. Esto podría ocasionar inconsistencias, por lo que es recomendable revisar y corregir los datos para asegurar su precisión.
		</Message>}
		<Table
			details
			title='Datos de liquidación'
			style={{ maxWidth: 500, margin: 'auto', marginTop: 20 }}
		>
			<Table.Row data={['ID', payout.payout_id]}/>
			<Table.Row data={['Creado', moment.unix(payout.date_created).format('DD/MMM/YY HH:mm')]}/>
			<Table.Row data={['Creador', payout.user_creator_name]}/>
			<Table.Row data={['Total', '$' + addCommas(totalDebt)]}/>
			<Table.Row data={['Estatus', <div className={classNames("fr label", {
							green: paid,
							red: payout.cancelled,
							yellow: !paid
						})}>
							{payout.cancelled ? 'Cancelado' : paid ? 'Pagado' : 'No Pagado'}
						</div>]}/>
		</Table>
		<Header text='Empleados' centered={false} style={{ marginTop: 15 }} size='small' />
		{payout.users.length !== 0 ? (
			payout.users.map(u=>(
				<Card className='worker' key={`LQPLH-${u.user_id}`} onClick={() => showWorkerModal(u)}>
					<div className="left">
						<div className="meta id">Empleado #{u.user_id}</div>
						<div className="name">{u.user_name}</div>
						<div className="job-name">{u.job_name}</div>
					</div>
					<div className='right'>
						<div className="amount">${addCommas(u.amount)}</div>
						<div className={classNames("fr label", {
							green: u.paid && !u.cancelled,
							yellow: !u.paid && !u.cancelled,
							red: u.cancelled
						})}>
							{u.cancelled ? 'Cancelado' : u.paid ? 'Pagado' : 'No Pagado'}
						</div>
					</div>
				</Card>
			))
		) : <Message text='No existen liquidaciónes.' centered style={{ fontWeight: 'bold', marginTop: 15 }} /> }
		<Modal open={workerModal} onClose={bindClick(setWorkerModal)} size='tiny'>
			<Modal.Header>Información de empleado</Modal.Header>
			{selectedWorker && <Modal.Content>
				<Table 
					details
					title='Empleado'
					button={!selectedWorker.paid && hasAccess(BusinessAccess.ACCOUNTING.EDIT_USER_PAYMENT) &&
						<Button icon iconName='pencil' size='small' onClick={bindClick(setEditWorker, !editWorker)}/>
					}
				>
					<Table.Row data={['Nombre', selectedWorker.user_name]}/>
					<Table.Row data={['Pago', !editWorker ? '$' + addCommas(selectedWorker.amount) : <Input value={selectedWorker.amount} onChange={onWorkerEdit('amount')} inputType='number' placeholder='Ingresa el monto'/>]}/>
					<Table.Row data={['Estatus', <div className={classNames("fr label", {
							red: selectedWorker.cancelled,
							green: selectedWorker.paid && !selectedWorker.cancelled,
							yellow: !selectedWorker.paid && !selectedWorker.cancelled
						})}>
							{selectedWorker.cancelled ? 'Cancelado' : selectedWorker.paid ? 'Pagado' : 'No Pagado'}
						</div>]}/>
					{selectedWorker.paid ? <>
						<Table.Row data={['Fecha de pago', moment.unix(selectedWorker.date_paid).format('DD/MMM/YY HH:mm')]}/>
					</> : null
					}
				</Table>
				{!selectedWorker.cancelled && <div style={{ display: 'flex' , padding: 20, marginTop: 20 }}>
					{!selectedWorker.paid && !selectedWorker.cancelled && hasAccess(BusinessAccess.ACCOUNTING.PAID_USER_PAYMENT) && <Button color='green' text='Marcar Pagado' onClick={bindClick(setPaidWorkerModal, true)} size='small' style={{ display: 'block', margin: 'auto', width: 200 }}/> }
					{!selectedWorker.cancelled && hasAccess(BusinessAccess.ACCOUNTING.CANCEL_USER_PAYMENT) && <Button basic color='red' text='Cancelar Pago' size='small' style={{ display: 'block', margin: 'auto', width: 200 }} onClick={bindClick(setCancelWorkerModal, true)}/>}
				</div>}
				<Message list={modalPrompts} type='error' />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' onClick={bindClick(setWorkerModal)} />
				{editWorker && <Button text='Guardar' color='black' onClick={edit}/> }
			</Modal.Actions>
		</Modal>
		{hasAccess(BusinessAccess.ACCOUNTING.PAID_USER_PAYMENT) && <Modal open={paidWorkerModal} onClose={bindClick(setPaidWorkerModal)} size='mini'>
			<Modal.Header>Marcar como pagado</Modal.Header>
			{!!selectedWorker && (
				<Modal.Content>
					<Header text='¿Marcar como pagado?' />
					<Message type='info' centered>
						Esto marcara al empleado <b>{selectedWorker.user_name}</b> como pagado.
					</Message>
					<Message list={modalPrompts} type='error' />
				</Modal.Content>
			)}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setPaidWorkerModal)} />
				<Button text='Pagar' color='green' onClick={paidWorker}/>
			</Modal.Actions>
		</Modal>}
		{hasAccess(BusinessAccess.ACCOUNTING.CANCEL_USER_PAYMENT) && <Modal open={cancelWorkerModal} onClose={bindClick(setCancelWorkerModal)} size='mini'>
			<Modal.Header>Cancelar pago</Modal.Header>
			{!!selectedWorker && (
				<Modal.Content>
					<Header text='¿Cancelar pago?' />
					<Message type='info' centered>
						Esto cancelara el pago de <b>{selectedWorker.user_name}</b>.
					</Message>
					<Message list={modalPrompts} type='error' />
				</Modal.Content>
			)}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setCancelWorkerModal)} />
				<Button text='Cancelar' color='red' onClick={cancelWorker}/>
			</Modal.Actions>
		</Modal>}
   </div>
}

export default PostingPayoutInfo;